<!-- Enhanced InternetStatsModal.vue -->
<template>
  <div class="modal fade" :class="{ show: show }" :style="{ display: show ? 'block' : 'none' }" tabindex="-1" role="dialog">
    <div class="modal-dialog modal-xl modal-dialog-centered" role="document">
      <div class="modal-content">
        <div class="modal-header text-white">
          <h5 class="modal-title">
            <i class="mdi mdi-chart-bar me-2"></i>Kenya Internet Coverage Analytics & Provider Comparison
          </h5>
          <button type="button" class="btn-close btn-close-white" @click="$emit('close')"></button>
        </div>
        <div class="modal-body">
          <!-- Executive Summary Cards -->
          <div class="row mb-4">
            <div class="col-md-3 mb-3">
              <div class="card border-primary h-100">
                <div class="card-body text-center">
                  <i class="mdi mdi-access-point-network text-primary" style="font-size: 48px;"></i>
                  <h3 class="mt-2 mb-1 text-primary">{{ stats.totalPoints || 0 }}</h3>
                  <p class="text-muted mb-0">Total Coverage Points</p>
                  <small class="text-success">
                    <i class="mdi mdi-trending-up"></i> +{{ getGrowthRate() }}% this month
                  </small>
                </div>
              </div>
            </div>

            <div class="col-md-3 mb-3">
              <div class="card border-success h-100">
                <div class="card-body text-center">
                  <i class="mdi mdi-domain text-success" style="font-size: 48px;"></i>
                  <h3 class="mt-2 mb-1 text-success">{{ activeProviders.length }}</h3>
                  <p class="text-muted mb-0">Active Providers</p>
                  <small class="text-info">
                    <i class="mdi mdi-information"></i> Multi-provider coverage
                  </small>
                </div>
              </div>
            </div>

            <div class="col-md-3 mb-3">
              <div class="card border-info h-100">
                <div class="card-body text-center">
                  <i class="mdi mdi-speedometer text-info" style="font-size: 48px;"></i>
                  <h3 class="mt-2 mb-1 text-info">{{ averageSpeed }} Mbps</h3>
                  <p class="text-muted mb-0">Average Speed</p>
                  <small class="text-warning">
                    <i class="mdi mdi-lightning-bolt"></i> Peak: {{ maxSpeed }} Mbps
                  </small>
                </div>
              </div>
            </div>

            <div class="col-md-3 mb-3">
              <div class="card border-warning h-100">
                <div class="card-body text-center">
                  <i class="mdi mdi-map-marker-radius text-warning" style="font-size: 48px;"></i>
                  <h3 class="mt-2 mb-1 text-warning">{{ regionCount }}</h3>
                  <p class="text-muted mb-0">Regions Covered</p>
                  <small class="text-success">
                    <i class="mdi mdi-check"></i> {{ coveragePercentage }}% coverage
                  </small>
                </div>
              </div>
            </div>
          </div>

          <!-- Provider Market Share and Performance -->
          <div class="row mb-4">
            <div class="col-lg-6 mb-4">
              <div class="card h-100">
                <div class="card-header bg-light d-flex justify-content-between align-items-center">
                  <h6 class="mb-0">Provider Market Share</h6>
                  <div class="btn-group btn-group-sm">
                    <button class="btn btn-outline-primary active" @click="marketShareView = 'points'">By Points</button>
                    <button class="btn btn-outline-primary" @click="marketShareView = 'regions'">By Regions</button>
                  </div>
                </div>
                <div class="card-body">
                  <canvas id="providerMarketChart" height="300"></canvas>

                  <!-- Provider Legend with Details -->
                  <div class="provider-legend mt-3">
                    <div class="row">
                      <div class="col-6" v-for="provider in topProviders" :key="provider.name">
                        <div class="d-flex align-items-center mb-2">
                          <div class="provider-color-indicator me-2" :style="`background-color: ${getProviderColor(provider.name)}`"></div>
                          <img :src="getProviderLogo(provider.name)" width="20" height="20" class="rounded me-2">
                          <div class="flex-grow-1">
                            <div class="fw-bold small">{{ provider.name }}</div>
                            <div class="text-muted small">{{ provider.totalPoints }} points • {{ getMarketSharePercentage(provider) }}%</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-lg-6 mb-4">
              <div class="card h-100">
                <div class="card-header bg-light">
                  <h6 class="mb-0">Provider Performance Comparison</h6>
                </div>
                <div class="card-body">
                  <canvas id="providerPerformanceChart" height="300"></canvas>

                  <!-- Performance Metrics -->
                  <div class="performance-metrics mt-3">
                    <div class="row text-center">
                      <div class="col-4">
                        <div class="text-success">
                          <i class="mdi mdi-speedometer"></i>
                          <div class="fw-bold">Speed Leader</div>
                          <div class="small">{{ speedLeader.name }}</div>
                          <div class="text-muted small">{{ speedLeader.avgSpeed }} Mbps</div>
                        </div>
                      </div>
                      <div class="col-4">
                        <div class="text-primary">
                          <i class="mdi mdi-map-marker-multiple"></i>
                          <div class="fw-bold">Coverage Leader</div>
                          <div class="small">{{ coverageLeader.name }}</div>
                          <div class="text-muted small">{{ coverageLeader.totalPoints }} points</div>
                        </div>
                      </div>
                      <div class="col-4">
                        <div class="text-warning">
                          <i class="mdi mdi-currency-usd"></i>
                          <div class="fw-bold">Best Value</div>
                          <div class="small">{{ valueLeader.name }}</div>
                          <div class="text-muted small">KES {{ valueLeader.avgCost }}/mo</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Technology Distribution and Regional Analysis -->
          <div class="row mb-4">
            <div class="col-lg-8 mb-4">
              <div class="card h-100">
                <div class="card-header bg-light">
                  <h6 class="mb-0">Technology Distribution by Provider</h6>
                </div>
                <div class="card-body">
                  <canvas id="technologyDistributionChart" height="250"></canvas>
                </div>
              </div>
            </div>

            <div class="col-lg-4 mb-4">
              <div class="card h-100">
                <div class="card-header bg-light">
                  <h6 class="mb-0">Technology Summary</h6>
                </div>
                <div class="card-body">
                  <div class="technology-stats">
                    <div class="stat-item mb-3" v-for="tech in technologyStats" :key="tech.name">
                      <div class="d-flex justify-content-between align-items-center mb-1">
                        <span class="fw-bold">
                          <i :class="getTechnologyIcon(tech.name)" class="me-1"></i>
                          {{ tech.name }}
                        </span>
                        <span class="badge" :class="`bg-${getTechnologyColor(tech.name)}`">
                          {{ tech.count }}
                        </span>
                      </div>
                      <div class="progress" style="height: 8px;">
                        <div class="progress-bar"
                             :class="`bg-${getTechnologyColor(tech.name)}`"
                             :style="`width: ${(tech.count / stats.totalPoints) * 100}%`">
                        </div>
                      </div>
                      <div class="d-flex justify-content-between small text-muted mt-1">
                        <span>Avg Speed: {{ tech.avgSpeed }} Mbps</span>
                        <span>{{ ((tech.count / stats.totalPoints) * 100).toFixed(1) }}%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Regional Coverage Analysis -->
          <div class="row mb-4">
            <div class="col-12">
              <div class="card">
                <div class="card-header bg-light d-flex justify-content-between align-items-center">
                  <h6 class="mb-0">Regional Coverage & Provider Presence</h6>
                  <button class="btn btn-sm btn-outline-primary" @click="toggleRegionalView">
                    <i class="mdi mdi-swap-horizontal"></i> Switch View
                  </button>
                </div>
                <div class="card-body">
                  <div class="table-responsive">
                    <table class="table table-hover table-sm">
                      <thead>
                      <tr>
                        <th>Region</th>
                        <th class="text-center">Total Points</th>
                        <th class="text-center">Providers</th>
                        <th class="text-center">Avg Speed</th>
                        <th class="text-center">Dominant Provider</th>
                        <th class="text-center">Technology Mix</th>
                        <th class="text-center">Coverage Score</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="region in regionalAnalysis" :key="region.name">
                        <td>
                          <i class="mdi mdi-map-marker text-primary me-1"></i>
                          <strong>{{ region.name }}</strong>
                        </td>
                        <td class="text-center">
                          <span class="badge bg-info">{{ region.totalPoints }}</span>
                        </td>
                        <td class="text-center">
                          <div class="provider-avatars">
                            <img v-for="provider in region.providers.slice(0, 3)"
                                 :key="provider"
                                 :src="getProviderLogo(provider)"
                                 width="20" height="20"
                                 class="rounded provider-avatar"
                                 :title="provider">
                            <span v-if="region.providers.length > 3" class="badge bg-secondary ms-1">
                                +{{ region.providers.length - 3 }}
                              </span>
                          </div>
                        </td>
                        <td class="text-center">
                          <span class="fw-bold text-success">{{ region.avgSpeed }} Mbps</span>
                        </td>
                        <td class="text-center">
                          <div class="d-flex align-items-center justify-content-center">
                            <img :src="getProviderLogo(region.dominantProvider)" width="20" height="20" class="rounded me-1">
                            <span class="small">{{ region.dominantProvider }}</span>
                          </div>
                        </td>
                        <td class="text-center">
                          <div class="tech-mix">
                              <span v-for="tech in region.technologies" :key="tech"
                                    class="badge badge-sm me-1"
                                    :class="`bg-${getTechnologyColor(tech)}`">
                                {{ tech }}
                              </span>
                          </div>
                        </td>
                        <td class="text-center">
                          <div class="coverage-score">
                            <div class="score-circle" :class="getCoverageScoreClass(region.coverageScore)">
                              {{ region.coverageScore }}
                            </div>
                          </div>
                        </td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Cost Analysis Section -->
          <div class="row mb-4">
            <div class="col-lg-8 mb-4">
              <div class="card h-100">
                <div class="card-header bg-light">
                  <h6 class="mb-0">Cost vs Speed Analysis by Provider</h6>
                </div>
                <div class="card-body">
                  <canvas id="costSpeedChart" height="300"></canvas>
                </div>
              </div>
            </div>

            <div class="col-lg-4 mb-4">
              <div class="card h-100">
                <div class="card-header bg-light">
                  <h6 class="mb-0">Value Analysis</h6>
                </div>
                <div class="card-body">
                  <div class="value-analysis">
                    <div class="mb-3">
                      <h6 class="text-success">Best Value Providers</h6>
                      <div v-for="provider in valueProviders" :key="provider.name" class="d-flex align-items-center mb-2">
                        <img :src="getProviderLogo(provider.name)" width="25" height="25" class="rounded me-2">
                        <div class="flex-grow-1">
                          <div class="fw-bold small">{{ provider.name }}</div>
                          <div class="text-muted small">
                            KES {{ provider.avgCost }}/mo • {{ provider.avgSpeed }} Mbps
                          </div>
                        </div>
                        <div class="value-score badge bg-success">
                          {{ calculateValueScore(provider) }}
                        </div>
                      </div>
                    </div>

                    <div class="mb-3">
                      <h6 class="text-warning">Cost Ranges</h6>
                      <div class="cost-ranges">
                        <div class="cost-range mb-2">
                          <span class="badge bg-success me-2">Budget</span>
                          <span class="small">KES 1,000 - 3,000/mo</span>
                        </div>
                        <div class="cost-range mb-2">
                          <span class="badge bg-warning me-2">Mid-range</span>
                          <span class="small">KES 3,000 - 7,000/mo</span>
                        </div>
                        <div class="cost-range mb-2">
                          <span class="badge bg-danger me-2">Premium</span>
                          <span class="small">KES 7,000+ /mo</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-success" @click="downloadDetailedReport">
            <i class="mdi mdi-file-chart me-1"></i> Download Analytics Report
          </button>
          <button type="button" class="btn btn-info" @click="shareAnalytics">
            <i class="mdi mdi-share me-1"></i> Share Analytics
          </button>
          <button type="button" class="btn btn-secondary" @click="$emit('close')">
            <i class="mdi mdi-close me-1"></i> Close
          </button>
        </div>
      </div>
    </div>
  </div>
  <div v-if="show" class="modal-backdrop fade show"></div>
</template>

<script>
/* eslint-disable */
import Chart from 'chart.js/auto';

export default {
  name: 'InternetStatsModal',
  props: {
    show: { type: Boolean, default: false },
    stats: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      marketShareView: 'points',
      regionalView: 'coverage',
      providerMarketChart: null,
      providerPerformanceChart: null,
      technologyDistributionChart: null,
      costSpeedChart: null,
      providerColors: {
        'Safaricom': '#00AA44',
        'Zuku (Wananchi Group)': '#FF6600',
        'Airtel Kenya': '#FF0000',
        'Telkom Kenya': '#0066CC',
        'JTL Faiba': '#8B008B',
        'Poa Internet': '#00CCCC',
        'Liquid Telecom': '#FF69B4',
        'Mawingu Networks': '#32CD32'
      }
    }
  },
  computed: {
    activeProviders() {
      return this.stats.providerStats || [];
    },

    averageSpeed() {
      if (!this.stats.connectionStats || this.stats.connectionStats.length === 0) return 0;
      const totalSpeed = this.stats.connectionStats.reduce((sum, stat) => {
        return sum + (parseFloat(stat.avgSpeed) * stat.count);
      }, 0);
      const totalCount = this.stats.connectionStats.reduce((sum, stat) => sum + stat.count, 0);
      return totalCount > 0 ? (totalSpeed / totalCount).toFixed(1) : 0;
    },

    maxSpeed() {
      if (!this.stats.connectionStats || this.stats.connectionStats.length === 0) return 0;
      const speeds = this.stats.connectionStats.map(stat => parseFloat(stat.avgSpeed));
      return Math.max(...speeds).toFixed(1);
    },

    regionCount() {
      return this.stats.regionStats ? this.stats.regionStats.length : 0;
    },

    coveragePercentage() {
      // Mock calculation - in real app this would be based on actual coverage data
      return Math.min(95, (this.regionCount * 12)).toFixed(0);
    },

    topProviders() {
      return [...this.activeProviders].sort((a, b) => b.totalPoints - a.totalPoints).slice(0, 6);
    },

    speedLeader() {
      if (this.activeProviders.length === 0) return { name: 'N/A', avgSpeed: 0 };
      return [...this.activeProviders].sort((a, b) => parseFloat(b.avgSpeed) - parseFloat(a.avgSpeed))[0];
    },

    coverageLeader() {
      if (this.activeProviders.length === 0) return { name: 'N/A', totalPoints: 0 };
      return [...this.activeProviders].sort((a, b) => b.totalPoints - a.totalPoints)[0];
    },

    valueLeader() {
      const providersWithCost = this.activeProviders.filter(p => p.avgCost > 0);
      if (providersWithCost.length === 0) return { name: 'N/A', avgCost: 0 };
      return [...providersWithCost].sort((a, b) => parseFloat(a.avgCost) - parseFloat(b.avgCost))[0];
    },

    valueProviders() {
      return this.activeProviders
          .filter(p => p.avgCost > 0)
          .map(p => ({ ...p, valueScore: this.calculateValueScore(p) }))
          .sort((a, b) => b.valueScore - a.valueScore)
          .slice(0, 3);
    },

    technologyStats() {
      if (!this.stats.connectionStats) return [];
      return this.stats.connectionStats.map(stat => ({
        name: stat.connectionType,
        count: stat.count,
        avgSpeed: stat.avgSpeed
      }));
    },

    regionalAnalysis() {
      if (!this.stats.regionStats) return [];

      return this.stats.regionStats.map(region => ({
        name: region.region,
        totalPoints: region.count,
        providers: this.getRegionProviders(region.region),
        avgSpeed: this.getRegionAvgSpeed(region.region),
        dominantProvider: this.getRegionDominantProvider(region.region),
        technologies: this.getRegionTechnologies(region.region),
        coverageScore: this.calculateRegionCoverageScore(region)
      }));
    }
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.createCharts();
        });
      }
    }
  },
  methods: {
    createCharts() {
      this.createProviderMarketChart();
      this.createProviderPerformanceChart();
      this.createTechnologyDistributionChart();
      this.createCostSpeedChart();
    },

    createProviderMarketChart() {
      const ctx = document.getElementById('providerMarketChart');
      if (ctx && this.activeProviders.length > 0) {
        if (this.providerMarketChart) {
          this.providerMarketChart.destroy();
        }

        const data = this.activeProviders.map(p => p.totalPoints);
        const labels = this.activeProviders.map(p => p.name);
        const colors = this.activeProviders.map(p => this.getProviderColor(p.name));

        this.providerMarketChart = new Chart(ctx, {
          type: 'doughnut',
          data: {
            labels: labels,
            datasets: [{
              data: data,
              backgroundColor: colors,
              borderWidth: 3,
              borderColor: '#fff'
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  label: (context) => {
                    const total = data.reduce((a, b) => a + b, 0);
                    const percentage = ((context.parsed / total) * 100).toFixed(1);
                    return `${context.label}: ${context.parsed} points (${percentage}%)`;
                  }
                }
              }
            }
          }
        });
      }
    },

    createProviderPerformanceChart() {
      const ctx = document.getElementById('providerPerformanceChart');
      if (ctx && this.activeProviders.length > 0) {
        if (this.providerPerformanceChart) {
          this.providerPerformanceChart.destroy();
        }

        const labels = this.activeProviders.map(p => p.name.split(' ')[0]); // Shortened names
        const speedData = this.activeProviders.map(p => parseFloat(p.avgSpeed));
        const coverageData = this.activeProviders.map(p => p.totalPoints);

        this.providerPerformanceChart = new Chart(ctx, {
          type: 'bar',
          data: {
            labels: labels,
            datasets: [
              {
                label: 'Avg Speed (Mbps)',
                data: speedData,
                backgroundColor: 'rgba(40, 167, 69, 0.8)',
                borderColor: 'rgba(40, 167, 69, 1)',
                borderWidth: 1,
                yAxisID: 'y'
              },
              {
                label: 'Coverage Points',
                data: coverageData,
                backgroundColor: 'rgba(23, 162, 184, 0.8)',
                borderColor: 'rgba(23, 162, 184, 1)',
                borderWidth: 1,
                yAxisID: 'y1'
              }
            ]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              y: {
                type: 'linear',
                display: true,
                position: 'left',
                title: { display: true, text: 'Speed (Mbps)' }
              },
              y1: {
                type: 'linear',
                display: true,
                position: 'right',
                title: { display: true, text: 'Coverage Points' },
                grid: { drawOnChartArea: false }
              }
            }
          }
        });
      }
    },

    createTechnologyDistributionChart() {
      const ctx = document.getElementById('technologyDistributionChart');
      if (ctx && this.technologyStats.length > 0) {
        if (this.technologyDistributionChart) {
          this.technologyDistributionChart.destroy();
        }

        // Create stacked bar chart showing technology distribution by provider
        const technologies = this.technologyStats.map(t => t.name);
        const datasets = this.activeProviders.map(provider => ({
          label: provider.name,
          data: technologies.map(tech => provider.connectionTypes[tech] || 0),
          backgroundColor: this.getProviderColor(provider.name) + '80',
          borderColor: this.getProviderColor(provider.name),
          borderWidth: 1
        }));

        this.technologyDistributionChart = new Chart(ctx, {
          type: 'bar',
          data: {
            labels: technologies,
            datasets: datasets
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              x: { stacked: true },
              y: { stacked: true, title: { display: true, text: 'Number of Points' } }
            }
          }
        });
      }
    },

    createCostSpeedChart() {
      const ctx = document.getElementById('costSpeedChart');
      if (ctx) {
        if (this.costSpeedChart) {
          this.costSpeedChart.destroy();
        }

        const data = this.activeProviders.filter(p => p.avgCost > 0).map(p => ({
          x: parseFloat(p.avgCost),
          y: parseFloat(p.avgSpeed),
          label: p.name,
          backgroundColor: this.getProviderColor(p.name)
        }));

        this.costSpeedChart = new Chart(ctx, {
          type: 'scatter',
          data: {
            datasets: [{
              label: 'Providers',
              data: data,
              backgroundColor: data.map(d => d.backgroundColor + '80'),
              borderColor: data.map(d => d.backgroundColor),
              pointRadius: 10,
              pointHoverRadius: 12
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              x: {
                title: { display: true, text: 'Average Cost (KES/month)' },
                beginAtZero: true
              },
              y: {
                title: { display: true, text: 'Average Speed (Mbps)' },
                beginAtZero: true
              }
            },
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  label: (context) => {
                    const point = context.raw;
                    return `${point.label}: KES ${point.x}/mo, ${point.y} Mbps`;
                  }
                }
              }
            }
          }
        });
      }
    },

    getProviderColor(name) {
      return this.providerColors[name] || '#666666';
    },

    getProviderLogo(name) {
      const logos = {
        'Safaricom': 'assets/images/providers/safaricom-logo.png',
        'Zuku (Wananchi Group)': 'assets/images/providers/zuku-logo.png',
        'Airtel Kenya': 'assets/images/providers/airtel-logo.png',
        'Telkom Kenya': 'assets/images/providers/telkom-logo.png',
        'JTL Faiba': 'assets/images/providers/faiba-logo.png',
        'Poa Internet': 'assets/images/providers/poa-logo.png',
        'Liquid Telecom': 'assets/images/providers/liquid-logo.png',
        'Mawingu Networks': 'assets/images/providers/mawingu-logo.png'
      };
      return logos[name] || 'assets/images/providers/safaricom-logo.png';
    },

    getTechnologyIcon(tech) {
      const icons = {
        'Fiber': 'mdi mdi-ethernet',
        '4G': 'mdi mdi-signal-4g',
        '5G': 'mdi mdi-signal-5g',
        'Wireless': 'mdi mdi-wifi',
        'Satellite': 'mdi mdi-satellite-variant'
      };
      return icons[tech] || 'mdi mdi-router-network';
    },

    getTechnologyColor(tech) {
      const colors = {
        'Fiber': 'primary',
        '4G': 'success',
        '5G': 'info',
        'Wireless': 'warning',
        'Satellite': 'secondary'
      };
      return colors[tech] || 'secondary';
    },

    getMarketSharePercentage(provider) {
      const total = this.activeProviders.reduce((sum, p) => sum + p.totalPoints, 0);
      return total > 0 ? ((provider.totalPoints / total) * 100).toFixed(1) : 0;
    },

    getGrowthRate() {
      // Mock growth rate - in real app this would come from historical data
      return Math.floor(Math.random() * 20) + 5;
    },

    calculateValueScore(provider) {
      if (!provider.avgCost || provider.avgCost <= 0) return 0;
      const speedPerKes = parseFloat(provider.avgSpeed) / parseFloat(provider.avgCost);
      return (speedPerKes * 1000).toFixed(1); // Speed per 1000 KES
    },

    getRegionProviders(regionName) {
      // Mock data - in real app this would query actual provider presence by region
      const allProviders = ['Safaricom', 'Airtel Kenya', 'Telkom Kenya', 'Zuku (Wananchi Group)'];
      const count = Math.floor(Math.random() * 3) + 2; // 2-4 providers per region
      return allProviders.slice(0, count);
    },

    getRegionAvgSpeed(regionName) {
      // Mock calculation - in real app this would calculate from actual data
      return (Math.random() * 200 + 50).toFixed(1);
    },

    getRegionDominantProvider(regionName) {
      const providers = this.getRegionProviders(regionName);
      return providers[0]; // First provider is dominant
    },

    getRegionTechnologies(regionName) {
      const allTechs = ['Fiber', '4G', 'Wireless', 'Satellite'];
      const count = Math.floor(Math.random() * 3) + 1; // 1-3 technologies per region
      return allTechs.slice(0, count);
    },

    calculateRegionCoverageScore(region) {
      // Calculate coverage score based on points, providers, and technologies
      const pointsScore = Math.min(50, region.count * 5);
      const providerScore = this.getRegionProviders(region.region).length * 10;
      const techScore = this.getRegionTechnologies(region.region).length * 5;

      return Math.min(100, pointsScore + providerScore + techScore);
    },

    getCoverageScoreClass(score) {
      if (score >= 80) return 'score-excellent';
      if (score >= 60) return 'score-good';
      if (score >= 40) return 'score-average';
      return 'score-poor';
    },

    toggleRegionalView() {
      this.regionalView = this.regionalView === 'coverage' ? 'provider' : 'coverage';
    },

    downloadDetailedReport() {
      this.$swal({
        icon: 'info',
        title: 'Generating Analytics Report',
        text: 'Your comprehensive provider analytics report is being generated...',
        timer: 3000,
        showConfirmButton: false
      });

      setTimeout(() => {
        this.$swal({
          icon: 'success',
          title: 'Report Ready',
          html: `
            <p>Your analytics report has been generated successfully!</p>
            <p><strong>Report includes:</strong></p>
            <ul class="text-start">
              <li>Provider market share analysis</li>
              <li>Speed and performance comparisons</li>
              <li>Cost-benefit analysis</li>
              <li>Regional coverage breakdown</li>
              <li>Technology distribution charts</li>
              <li>Growth trends and projections</li>
            </ul>
          `,
          confirmButtonText: 'Download Now'
        });
      }, 3000);
    },

    shareAnalytics() {
      const shareData = {
        title: 'Kenya Internet Provider Analytics',
        text: `Analytics for ${this.activeProviders.length} internet service providers across ${this.regionCount} regions`,
        url: window.location.href
      };

      if (navigator.share) {
        navigator.share(shareData);
      } else {
        // Fallback to clipboard
        navigator.clipboard.writeText(shareData.url).then(() => {
          this.$swal({
            icon: 'success',
            title: 'Link Copied',
            text: 'Analytics link copied to clipboard!',
            timer: 2000
          });
        });
      }
    }
  },

  beforeDestroy() {
    if (this.providerMarketChart) this.providerMarketChart.destroy();
    if (this.providerPerformanceChart) this.providerPerformanceChart.destroy();
    if (this.technologyDistributionChart) this.technologyDistributionChart.destroy();
    if (this.costSpeedChart) this.costSpeedChart.destroy();
  }
}
</script>

<style scoped>
.modal.show { display: block; }
.modal-xl { max-width: 95%; }

.provider-color-indicator {
  width: 4px;
  height: 20px;
  border-radius: 2px;
  display: inline-block;
}

.provider-legend {
  max-height: 200px;
  overflow-y: auto;
}

.provider-avatars {
  display: flex;
  align-items: center;
  justify-content: center;
}

.provider-avatar {
  margin-left: -5px;
  border: 2px solid white;
}

.provider-avatar:first-child {
  margin-left: 0;
}

.score-circle {
  width: 35px;
  height: 35px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 0.8rem;
  color: white;
  margin: 0 auto;
}

.score-excellent { background-color: #28a745; }
.score-good { background-color: #17a2b8; }
.score-average { background-color: #ffc107; color: #212529; }
.score-poor { background-color: #dc3545; }

.coverage-score {
  display: flex;
  justify-content: center;
  align-items: center;
}

.tech-mix {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 2px;
}

.technology-stats {
  max-height: 300px;
  overflow-y: auto;
}

.stat-item {
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.stat-item:last-child {
  border-bottom: none;
}

.performance-metrics {
  border-top: 1px solid #e9ecef;
  padding-top: 15px;
}

.value-analysis {
  max-height: 350px;
  overflow-y: auto;
}

.value-score {
  min-width: 45px;
  text-align: center;
}

.cost-ranges {
  padding: 10px;
  background-color: #f8f9fa;
  border-radius: 6px;
}

.cost-range {
  display: flex;
  align-items: center;
}

.table td {
  vertical-align: middle;
}

.table-sm td {
  padding: 0.5rem 0.75rem;
}

.badge-sm {
  font-size: 0.7rem;
  padding: 0.2rem 0.4rem;
}

.progress {
  background-color: #e9ecef;
}

.card {
  border: 1px solid #e3e6f0;
  box-shadow: 0 0.15rem 1.75rem 0 rgba(58, 59, 69, 0.15);
}

.card-header {
  background-color: #f8f9fc;
  border-bottom: 1px solid #e3e6f0;
}

.btn-group-sm > .btn {
  padding: 0.25rem 0.5rem;
  font-size: 0.8rem;
}

canvas {
  max-height: 350px;
}

.fw-bold {
  font-weight: 600 !important;
}

@media (max-width: 768px) {
  .modal-xl {
    max-width: 98%;
    margin: 10px auto;
  }

  .table-responsive {
    font-size: 0.85rem;
  }

  .score-circle {
    width: 25px;
    height: 25px;
    font-size: 0.7rem;
  }

  .provider-avatar {
    width: 15px;
    height: 15px;
  }

  .badge-sm {
    font-size: 0.6rem;
    padding: 0.1rem 0.3rem;
  }

  .btn-group-sm > .btn {
    padding: 0.2rem 0.4rem;
    font-size: 0.7rem;
  }

  canvas {
    max-height: 250px;
  }
}

.text-white-50 {
  color: rgba(255, 255, 255, 0.5) !important;
}
</style>